import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Row } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import { Button } from "@/components/ui/button/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/dropdown-menu";
import { CompanyHolidayList } from "./schema";
import {
  setActiveCompanyHolidayList,
  deleteCompanyHolidayList,
} from "@/config/mutation/company-holiday-mutations";
import { toast } from "sonner";

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const adminKey = Cookies.get("key") as string;
  const originData = row.original as CompanyHolidayList;

  const setActiveMutation = useMutation({
    mutationFn: setActiveCompanyHolidayList(adminKey),
    onSuccess: () => {
      toast.success("Company holiday list set as active");
      queryClient.invalidateQueries({ queryKey: ["company-holidays"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to set as active");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCompanyHolidayList(adminKey),
    onSuccess: () => {
      toast.success("Company holiday list deleted");
      queryClient.invalidateQueries({ queryKey: ["company-holidays"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to delete");
    },
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem
          onMouseDown={() => {
            navigate(`/admin/company-holidays/${originData.id}`);
          }}
        >
          Edit
        </DropdownMenuItem>
        {!originData.isActive && (
          <DropdownMenuItem
            onMouseDown={() => {
              setActiveMutation.mutate(originData.id);
            }}
          >
            Set as Active
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-red-600"
          onMouseDown={() => {
            if (confirm("Are you sure you want to delete this holiday list?")) {
              deleteMutation.mutate(originData.id);
            }
          }}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
